"use client";

import Link from "next/link";
import { useState } from "react";
import { Banner, Button, Dialog, Field, IdText } from "@/components/ui";
import { call, client } from "@/lib/api";
import { todayLocal } from "@/lib/format";
import { useAsync } from "@/lib/hooks";
import { errorText, useT } from "@/lib/i18n";
import { useToast } from "@/lib/toast";
import type { Unit } from "@/lib/types";

/** Admin: issue one or many Netro IDs for a type and size. */
export function IssueDialog({ open, onClose, onIssued }: { open: boolean; onClose: () => void; onIssued: () => void }) {
  const t = useT();
  const toast = useToast();
  const types = useAsync(() => call(client.GET("/api/types")), []);
  const [typeId, setTypeId] = useState<number | "">("");
  const detail = useAsync(
    async () => (typeId === "" ? null : await call(client.GET("/api/types/{type_id}", { params: { path: { type_id: typeId } } }))),
    [typeId],
  );
  const [stepId, setStepId] = useState<number | "">("");
  const [quantity, setQuantity] = useState(1);
  const [batch, setBatch] = useState("");
  const [date, setDate] = useState(todayLocal());
  const [sample, setSample] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [created, setCreated] = useState<Unit[] | null>(null);

  const producible = detail.data?.steps.filter((s) => s.producible) ?? [];

  function close() {
    setCreated(null);
    setError(null);
    onClose();
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (typeId === "" || stepId === "") return;
    setBusy(true);
    setError(null);
    try {
      const units = await call(
        client.POST("/api/units/issue", {
          body: { type_id: typeId, step_id: stepId, quantity, batch_code: batch.trim() || null, production_date: date, is_sample: sample },
        }),
      );
      setCreated(units);
      toast.ok(t("issue.done", { n: units.length }));
      onIssued();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} title={t("units.issue")} onClose={close}>
      {created ? (
        <div className="stack">
          <Banner kind="ok">{t("issue.done", { n: created.length })}</Banner>
          <ul className="stack-sm" style={{ paddingLeft: "1.1rem", margin: 0 }}>
            {created.slice(0, 20).map((u) => (
              <li key={u.id}>
                <Link href={`/units/${u.id}`}>
                  <IdText value={u.netro_id} />
                </Link>
              </li>
            ))}
          </ul>
          {created.length > 20 && <p className="muted small">{t("issue.more", { n: created.length - 20 })}</p>}
          <div className="dialog-foot">
            <Link className="btn" href="/factory">
              {t("nav.factory")}
            </Link>
            <Button variant="primary" onClick={close}>
              {t("common.close")}
            </Button>
          </div>
        </div>
      ) : (
        <form className="stack" onSubmit={submit}>
          {error != null && <Banner kind="danger">{errorText(t, error)}</Banner>}
          <Field label={t("issue.type")}>
            <select value={typeId} onChange={(e) => { setTypeId(e.target.value ? Number(e.target.value) : ""); setStepId(""); }} required>
              <option value="">{t("common.choose")}</option>
              {types.data?.filter((x) => x.active).map((x) => (
                <option key={x.id} value={x.id}>
                  {x.code} · {x.name}
                  {x.client_name ? ` · ${x.client_name}` : ""}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("issue.size")} hint={t("issue.size_hint")}>
            <select value={stepId} onChange={(e) => setStepId(e.target.value ? Number(e.target.value) : "")} required disabled={typeId === ""}>
              <option value="">{t("common.choose")}</option>
              {producible.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.size_m2} m² · {t("common.storeys", { n: s.storeys })}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid-form">
            <Field label={t("issue.quantity")}>
              <input type="number" min={1} max={500} value={quantity} onChange={(e) => setQuantity(Math.max(1, Math.min(500, Number(e.target.value) || 1)))} required />
            </Field>
            <Field label={t("issue.production_date")} hint={t("issue.date_hint")}>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </Field>
            <Field label={t("issue.batch")} hint={t("issue.batch_hint")} className="span-all">
              <input type="text" value={batch} onChange={(e) => setBatch(e.target.value)} maxLength={48} />
            </Field>
          </div>
          <label className="check">
            <input type="checkbox" checked={sample} onChange={(e) => setSample(e.target.checked)} />
            <span>{t("issue.sample")}</span>
          </label>
          <div className="dialog-foot">
            <Button onClick={close}>{t("common.cancel")}</Button>
            <Button type="submit" variant="primary" busy={busy} disabled={typeId === "" || stepId === ""}>
              {t("issue.submit", { n: quantity })}
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
